import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/api/client';
import { useAuth, getDefaultRoute } from '@/auth/AuthContext';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { BrandMark } from '@/components/BrandMark';
import { useToast } from '@/components/Feedback';
import { RoleShell } from '@/pages/NotificationPreferences';

const MIN_LENGTH = 10;

export function ChangePassword() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const forced = Boolean(user?.forcePasswordReset);

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (next.length < MIN_LENGTH) {
      setError(`New password must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (next !== confirm) {
      setError('The new passwords do not match.');
      return;
    }
    if (next === current) {
      setError('Choose a password that is different from your current one.');
      return;
    }
    setSaving(true);
    try {
      await authApi.changePassword(current, next);
      await refresh();
      toast('Password updated');
      navigate(user ? getDefaultRoute(user.role, user.permissions) : '/login', { replace: true });
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Could not change your password.');
    } finally {
      setSaving(false);
    }
  };

  const form = (
    <form className="change-pw-form" onSubmit={handleSubmit}>
      {error && <div className="form-error" role="alert">{error}</div>}
      <Input
        label={forced ? 'Temporary password' : 'Current password'}
        type="password"
        value={current}
        onChange={(e) => setCurrent(e.target.value)}
        required
        autoComplete="current-password"
        hint={forced ? 'The password from your invite or reset email.' : undefined}
      />
      <Input
        label="New password"
        type="password"
        value={next}
        onChange={(e) => setNext(e.target.value)}
        required
        minLength={MIN_LENGTH}
        autoComplete="new-password"
        hint={`At least ${MIN_LENGTH} characters`}
      />
      <Input
        label="Confirm new password"
        type="password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        required
        autoComplete="new-password"
      />
      <Button type="submit" variant={forced ? 'signal' : 'primary'} fullWidth={forced} loading={saving}>
        {forced ? 'Set password & continue' : 'Update password'}
      </Button>
      <style>{`
        .change-pw-form { max-width: 400px; }
        .change-pw-form .form-error {
          background: #FDE8E8;
          color: var(--color-urgent);
          padding: var(--space-sm) var(--space-md);
          border-radius: var(--radius-sm);
          margin-bottom: var(--space-md);
          font-size: 0.875rem;
        }
        .change-pw-card {
          background: var(--color-white);
          color: var(--color-ink);
          padding: var(--space-xl);
          border-radius: var(--radius-md);
          width: 100%;
          max-width: 440px;
          box-shadow: var(--shadow-md);
        }
        .change-pw-card .change-pw-form { max-width: none; }
        .change-pw-panel {
          background: var(--color-surface);
          color: var(--color-text);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-lg);
          max-width: 480px;
        }
      `}</style>
    </form>
  );

  // First sign-in with a temporary password: a focused screen with no navigation,
  // since every other route sends the user back here until the password is set.
  if (forced) {
    return (
      <div className="auth-page" style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-xl)', background: 'var(--color-ink)' }}>
        <div style={{ width: '100%', maxWidth: 440, marginBottom: 'var(--space-lg)' }}>
          <BrandMark variant="light" size="lg" product="DispatchDash" />
        </div>
        <div className="change-pw-card">
          <p className="kicker" style={{ color: 'var(--color-ink)' }}>Welcome{user?.name ? `, ${user.name}` : ''}</p>
          <h2 style={{ marginBottom: 'var(--space-sm)' }}>Choose your password</h2>
          <p className="text-muted" style={{ marginBottom: 'var(--space-lg)' }}>
            You signed in with a temporary password. Set your own to continue.
          </p>
          {form}
          <p style={{ marginTop: 'var(--space-md)', fontSize: '0.875rem', textAlign: 'center' }}>
            Not you?{' '}
            <button
              type="button"
              className="link-button"
              onClick={async () => {
                await logout();
                navigate('/login', { replace: true });
              }}
              style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-ink)', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
            >
              Sign out
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <RoleShell role={user?.role ?? ''}>
      <div className="page">
        <div className="page-header">
          <p className="kicker">Account</p>
          <h1>Change password</h1>
          <p className="text-muted">Signed in as {user?.email}</p>
        </div>
        <div className="change-pw-panel">{form}</div>
      </div>
    </RoleShell>
  );
}
